import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI } from '../Interface';
import itemGroupService from '../../service/itemGroup.service';
import { ItemGroupFields } from './ItemGroupFields';
import { ItemGroupReports } from './ItemGroupReports';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import { ItemGroupType } from '../../config/itemGroup';
import ItemGroupTabs from './ItemGroupTabs';

export const ItemGroupPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [roleExcludedItemGroup, setRoleExcludedItemGroup] = useState([]);
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    quantityUnit: [],
  });
  const [loading, setLoading] = useState(true);

  const def = {
    name: '',
    quantityUnitId: null,
    type: ItemGroupType.STOCKARTICLE,
    stockItemType: '',
    inPurchase: false,
    inSales: false,
    groupPrefix: '',
    roleExcludedItemGroup: [],
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    initDefaultValues();
    getRoleExcludedItemGroupLinks();
    setDefaultValues(def);
    setDefaultSelectedValues(def);
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('itemGroup');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues, roleExcludedItemGroup]);

  const initDefaultValues = () => {
    masterDataService
      .quantityUnitToForm()
      .then((data) => {
        const aData = {
          quantityUnit: data,
        };

        const acOptions = {
          quantityUnit: generateAutocompleteItems(aData.quantityUnit, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
        setLoading(false);
      })
      .finally(() => {});
  };

  const getRoleExcludedItemGroupLinks = () => {
    masterDataService.rolesToForm().then((data) => {
      const preparedData = data.map((role) => ({
        ...role,
        checked: false,
      }));

      setRoleExcludedItemGroup(preparedData);
      setInitValues((prev) => ({ ...prev, roleExcludedItemGroup: preparedData }));
      setDefaultValues((prev) => ({ ...prev, roleExcludedItemGroup: preparedData }));
      setDefaultSelectedValues((prev) => ({ ...prev, roleExcludedItemGroup: preparedData }));
    });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    quantityUnitId: Yup.string().required(),
    type: Yup.string().required(),
    stockItemType: Yup.string()
      .nullable()
      .test('stockItemType-required', 'Kötelező', function (value) {
        const { type } = this.parent;
        if (type === ItemGroupType.STOCKARTICLE) {
          return value !== null && value !== undefined;
        }
        return true;
      }),
    inPurchase: Yup.boolean(),
    inSales: Yup.boolean(),
    groupPrefix: Yup.string().nullable().max(3),
    roleExcludedItemGroup: Yup.array().of(
      Yup.object().shape({
        roleId: Yup.number().required(),
        roleName: Yup.string().required(),
        checked: Yup.boolean().required(),
      })
    ),
  });

  const valuesToForm = () => {
    const { id, name, quantityUnitId, type, stockItemType, inPurchase, inSales, groupPrefix, roleExcludedItemGroup: roleExcludedItemGroupValues } = selectedValues;

    const updatedRoleExcludedItemGroup = roleExcludedItemGroup?.map((role) => ({
      ...role,
      checked:
        roleExcludedItemGroupValues?.some((form) =>
          typeof form === 'object' && form !== null ? form.roleId === role.roleId : form === role.roleId
        ) || false,
    }));

    setInitValues({
      id,
      name,
      quantityUnitId,
      type,
      stockItemType,
      inPurchase,
      inSales,
      groupPrefix,
      roleExcludedItemGroup: updatedRoleExcludedItemGroup,
    });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      updatedBy: user.userId,
      roleExcludedItemGroup: values.roleExcludedItemGroup.filter((link) => link.checked).map((link) => link.roleId),
    };

    itemGroupService.updateData(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...values,
      createdBy: user.userId,
      roleExcludedItemGroup: values.roleExcludedItemGroup.filter((link) => link.checked).map((link) => link.roleId),
    };

    itemGroupService
      .createData(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    itemGroupService.deleteData(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  return (
    <>
      <FormBase
        name="ItemGroup"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <ItemGroupFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      autoCompleteOptions={autocompleteOptions}
                    />

                    <ItemGroupTabs disabled={viewField} />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          !loading && (
            <ItemGroupReports
              refetch={{ refetchTable, setRefetchTable }}
              values={{ selectedValues, setSelectedValues }}
              viewField={viewField}
              operatorsDisabled={operatorsDisabled}
              rowSelection={{ rowSelectionModel, setRowSelectionModel }}
              autoCompleteOptions={autocompleteOptions}
            />
          )
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
